import React, { useMemo } from "react";
import { Button, Modal, Space, Typography, message } from "antd";
import AdminPriceTraceSummary from "./AdminPriceTraceSummary";
import AdminPriceTraceAnomalies from "./AdminPriceTraceAnomalies";
import AdminPriceTraceTable from "./AdminPriceTraceTable";
import { detectPriceTraceAnomalies } from "../utils/priceTraceAnomalyDetector";
import { buildSummaryTextLines, traceToCsvRows } from "../utils/priceTraceFormatters";
import "./AdminPriceTraceModal.css";

const { Title, Text } = Typography;

const entityLabel = {
  hotel: "מלון",
  package: "חבילה",
  order: "הזמנה",
  organized_tour: "טיול מאורגן",
};

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    message.success("הועתק ללוח");
  } catch {
    message.error("העתקה נכשלה");
  }
}

export default function AdminPriceTraceModal({ open, onClose, trace }) {
  const anomalies = useMemo(() => {
    if (!trace) return [];
    const auto = detectPriceTraceAnomalies({
      steps: trace.steps,
      summary: trace.summary,
    });
    const existing = Array.isArray(trace.anomalies) ? trace.anomalies : [];
    const keys = new Set(existing.map((a) => a.code));
    const merged = [...existing];
    auto.forEach((a) => {
      if (!keys.has(a.code)) {
        keys.add(a.code);
        merged.push(a);
      }
    });
    return merged;
  }, [trace]);

  const traceWithAnomalies = useMemo(
    () => (trace ? { ...trace, anomalies } : null),
    [trace, anomalies]
  );

  if (!trace) return null;

  const etLabel = entityLabel[trace.entityType] || trace.entityType || "—";

  const onCopyJson = () => copyText(JSON.stringify(traceWithAnomalies, null, 2));

  const onCopySummary = () => copyText(buildSummaryTextLines(traceWithAnomalies));

  const onExportCsv = () => {
    const csv = traceToCsvRows(trace.steps);
    if (!csv) {
      message.warning("אין שורות לייצוא");
      return;
    }
    copyText(csv);
    message.success("CSV הועתק ללוח (הדבק בגיליון אלקטרוני)");
  };

  const width =
    typeof window !== "undefined" ? Math.min(1280, Math.max(360, window.innerWidth - 32)) : 1280;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      wrapClassName={trace?.entityType === "organized_tour" ? "apt-modal--organized-tour-rtl" : undefined}
      footer={
        <Space wrap>
          <Button onClick={onClose}>סגור</Button>
          <Button onClick={onCopyJson}>העתק trace כ־JSON</Button>
          <Button onClick={onCopySummary}>העתק סיכום</Button>
          <Button onClick={onExportCsv}>ייצוא CSV (העתקה)</Button>
        </Space>
      }
      width={width}
      className="apt-modal"
      destroyOnHidden
      centered
      title={
        <div className="apt-modal__head">
          <Title level={4} style={{ margin: 0 }}>
            Trace מחיר
          </Title>
          <Text type="secondary">
            {etLabel} · {trace.entityName || "—"}
            {trace.summary?.displayOrderNumberForAgent ? (
              <> · הזמנה: {trace.summary.displayOrderNumberForAgent}</>
            ) : null}
            {" "}
            · deal: {trace.entityId ?? "—"}
          </Text>
        </div>
      }
    >
      <div className="apt-modal__body">
        <AdminPriceTraceSummary summary={trace.summary} entityType={trace.entityType} />
        <div className="apt-section-title">חריגות</div>
        <AdminPriceTraceAnomalies anomalies={anomalies} />
        <div className="apt-section-title">שרשרת שלבים</div>
        <AdminPriceTraceTable steps={trace.steps} />
      </div>
    </Modal>
  );
}
